import {
  ChevronDown,
  ChevronUp,
  Clock,
  MoreHorizontal,
  Plus,
} from 'lucide-react';
import { useState } from 'react';

import type {
  Performance,
  Show,
} from '~entities/recital-manager/model/recital';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { cn } from '~/shared/shadcn/lib/utils';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import { formatRecitalTime } from '~shared/utils/recital-timezone';

interface ShowWithPerformances extends Show {
  performances?: Array<Performance>;
}

interface ShowSwitcherProps {
  shows: Array<ShowWithPerformances>;
  selectedShowId: string;
  onShowSelect: (showId: string) => void;
  onAddShow?: () => void;
  onPrintSchedule?: () => Promise<void> | void;
  timezone?: string;
}

export const ShowSwitcher = ({
  shows,
  selectedShowId,
  onShowSelect,
  onAddShow,
  onPrintSchedule,
  timezone = 'UTC',
}: ShowSwitcherProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { hasAdminAccess } = useCurrentUserRole();
  // Calculate total runtime for all shows
  const totalRuntime = shows.reduce((total, show) => {
    const showRuntime = (show.performances ?? []).reduce(
      (sum: number, perf: Performance) => sum + (perf.duration ?? 0),
      0,
    );
    return total + showRuntime;
  }, 0);

  const formatRuntime = (minutes: number) => {
    // Handle edge cases
    if (minutes <= 0) {
      return '0m';
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const formatShowTime = (show: ShowWithPerformances) => {
    // Show.showDate is the date/time as a string
    return formatRecitalTime(show.showDate, timezone, 'h:mm a');
  };

  const getShowRuntime = (show: ShowWithPerformances) => {
    const runtime = (show.performances ?? []).reduce(
      (sum: number, perf: Performance) => sum + (perf.duration ?? 0),
      0,
    );
    return formatRuntime(runtime);
  };

  return (
    <div className='space-y-4'>
      {/* Header with total runtime */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2 sm:gap-3'>
          <h3 className='text-lg font-semibold'>Shows</h3>
          <Badge variant='secondary' className='gap-1'>
            <Clock className='size-3 shrink-0' />
            Total: {formatRuntime(totalRuntime)}
          </Badge>
        </div>
        <div className='flex items-center gap-2 self-end sm:self-auto'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className='mr-2 size-4' />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ChevronDown className='mr-2 size-4' />
                    Expand All
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!onPrintSchedule}
                onClick={() => {
                  void onPrintSchedule?.();
                }}
              >
                Print Schedule
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Show Tabs */}
      {isExpanded && (
        <div className='grid grid-cols-1 gap-2 sm:flex sm:flex-wrap'>
          {shows.map((show, index) => {
            const isSelected = show._id === selectedShowId;
            const performanceCount = show.performances?.length ?? 0;

            return (
              <Button
                key={show._id}
                variant={isSelected ? 'default' : 'outline'}
                className={cn(
                  'h-auto w-full flex-col items-start p-4 text-left transition-all sm:w-auto sm:min-w-[200px] sm:max-w-full',
                  isSelected && 'ring-2 ring-primary ring-offset-2',
                )}
                onClick={() => {
                  onShowSelect(show._id);
                }}
              >
                <div className='flex w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
                  <span className='font-semibold'>Show {index + 1}</span>
                  {show.name ? (
                    <span className='text-xs capitalize text-muted-foreground break-words'>
                      ({show.name})
                    </span>
                  ) : null}
                </div>
                <div className='mt-2 flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs'>
                  <div className='flex items-center gap-1'>
                    <Clock className='size-3 shrink-0' />
                    <span>{formatShowTime(show)}</span>
                  </div>
                  <Badge variant='secondary' className='text-xs'>
                    {performanceCount}{' '}
                    {performanceCount === 1 ? 'Item' : 'Items'}
                  </Badge>
                  <span className='text-muted-foreground'>
                    {getShowRuntime(show)}
                  </span>
                </div>
              </Button>
            );
          })}

          {/* Add Show Button */}
          {onAddShow && hasAdminAccess && (
            <Button
              variant='outline'
              className='h-auto w-full flex-col items-center justify-center border-dashed p-4 sm:w-auto sm:min-w-[140px]'
              onClick={onAddShow}
            >
              <Plus className='mb-1 size-5' />
              <span className='text-sm'>Add Show</span>
            </Button>
          )}
        </div>
      )}

      {/* Collapsed state */}
      {!isExpanded && (
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Button
            variant='outline'
            size='sm'
            className='w-full sm:w-auto'
            onClick={() => {
              setIsExpanded(true);
            }}
          >
            <ChevronDown className='mr-2 size-4' />
            Show {shows.findIndex((s) => s._id === selectedShowId) + 1} of{' '}
            {shows.length}
          </Button>
          <span className='text-sm text-muted-foreground'>
            {shows.find((s) => s._id === selectedShowId)?.performances
              ?.length ?? 0}{' '}
            performances
          </span>
        </div>
      )}
    </div>
  );
};
